import type {
    User,
    Licenses,
    Orders,
    Order,
    AdminUser,
    AdminUsers,
    AdminOrders,
    AdminCoupons,
    AdminOrder,
    CreateUpdateAdminCouponPayload,
    AdminCoupon,
    Role,
    AdminProducts,
    AdminProduct,
    UpdateAdminProductPayload
} from "~~/types/auth";
import {defineStore} from 'pinia'
import {useXsrfToken} from "~/composables/useXsrfToken";
import { useCartStore } from "~~/stores/cart"

type UpdateProfilePayload = Pick<User,
    'name' | 'surname' | 'email' | 'phone' | 'address' | 'zip' | 'city' | 'kids'
>

type UpdateProfilePayloadAdmin = {
    name: string
    surname: string
    email: string
    phone: string | null
    address: string | null
    zip: string | null
    city: string | null
    kids: any[]
    roles: string[]
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null as User | null,
        licenses: null as Licenses | null,
        license: null as Licenses | null,
        _audioObjectUrls: {} as Record<string, string>,
        orders: null as Orders | null,
        adminUsers: null as AdminUsers | null,
        adminUser: null as AdminUser | null,
        adminOrders: null as AdminOrders | null,
        order: null as Order | null,
        adminOrder: null as Order | null,
        adminCoupons: null as AdminCoupons | null,
        adminCoupon: null as AdminCoupon | null,
        adminProducts: null as AdminProducts | null,
        adminProduct: null as AdminProduct | null,
        roles: null as Role[] | null,
        loading: false,
        error: null as string | null,
    }),

    actions: {
        async getCsrfCookie() {
            const { $api } = useNuxtApp()
            await $api('/sanctum/csrf-cookie', {
                baseURL: useRuntimeConfig().public.apiBase,
                credentials: 'include',
            })
        },

        async register(payload: { name: string, surname: string; email: string; password: string; password_confirmation: string }) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                await this.getCsrfCookie()
                await $api('/register', {
                    method: 'POST',
                    body: payload,
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-XSRF-TOKEN': useXsrfToken() || '',
                    },
                })
                await this.fetchUser()

                const cart = useCartStore()
                await cart.fetch()

                return this.user
            } catch (err: any) {
                this.error = err?.data?.message || 'Registration failed'
                throw err
            } finally {
                this.loading = false
            }
        },

        async login(payload: { email: string; password: string }) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                await this.getCsrfCookie()
                await $api('/login', {
                    method: 'POST',
                    body: {
                        ...payload,
                        remember: true,
                    },
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: { Accept: 'application/json', 'X-XSRF-TOKEN': useXsrfToken() || '' },
                })
                await this.fetchUser()

                const cart = useCartStore()
                await cart.fetch()

                return this.user
            } catch (err: any) {
                this.error = err?.data?.message || 'Login failed'
                throw err
            } finally {
                this.loading = false
            }
        },

        async forgottenPassword(payload: { email: string }) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                await this.getCsrfCookie()
                return await $api('/forgot-password', {
                    method: 'POST',
                    body: payload,
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: { Accept: 'application/json', 'X-XSRF-TOKEN': useXsrfToken() || '' },
                })
            } catch (err: any) {
                this.error = err?.data?.message || 'Email failed'
                return err
            } finally {
                this.loading = false
            }
        },

        async resetPassword(payload: { email: string, token: string, password: string, password_confirmation: string }) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                await this.getCsrfCookie()
                return await $api('/reset-password', {
                    method: 'POST',
                    body: payload,
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: { Accept: 'application/json', 'X-XSRF-TOKEN': useXsrfToken() || '' },
                })
            } catch (err: any) {
                this.error = err?.data?.message || 'Reset failed'
                throw err            // <<< IMPORTANT: propagate 4xx/5xx to the caller
            } finally {
                this.loading = false
            }
        },

        async fetchUser() {
            try {
                const { $api } = useNuxtApp()
                this.user = await $api<User>('/api/user', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
            } catch {
                this.user = null
            }
        },

        async fetchUserSSR() {
            try {
                const { $api } = useNuxtApp()
                this.user = await $api<User>('/api/user')
            } catch {
                this.user = null
            }
        },

        async fetchUserLicenses() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Licenses>('/api/user/licenses', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.licenses = data
                return data
            } catch {
                this.licenses = null
                return null
            }
        },

        async fetchUserLicensesSSR() {
            try {
                const { $api } = useNuxtApp()
                this.licenses = await $api<Licenses>('/api/user/licenses')
            } catch {
                this.licenses = null
            }
        },

        async fetchUserLicense(license_id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Licenses>(`/api/user/licenses/${license_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.license = data
                return data
            } catch {
                this.license = null
                return null
            }
        },

        async fetchUserOrder(order_id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Order>(`/api/user/orders/${order_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.order = data
                return data
            } catch {
                this.order = null
                return null
            }
        },

        async fetchAdminOrder(order_id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminOrder>(`/api/admin/orders/${order_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminOrder = data
                return data
            } catch {
                this.adminOrder = null
                return null
            }
        },

        async fetchUserOrders() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Orders>('/api/user/orders', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.orders = data
                return data
            } catch {
                this.orders = null
            }
        },

        async fetchAdminRoles() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Role[]>('/api/admin/roles', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.roles = data
                return data
            } catch {
                this.roles = null
            }
        },

        async fetchAdminUsers() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminUsers>('/api/admin/users', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminUsers = data
                return data
            } catch {
                this.adminUsers = null
            }
        },

        async fetchAdminUser(user_id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminUser>(`/api/admin/users/${user_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminUser = data
                return data
            } catch {
                this.adminUser = null
            }
        },

        async updateAdminUser(user_id: number, payload: UpdateProfilePayloadAdmin) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminUser>(`/api/admin/users/${user_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    method: 'PUT',
                    body: payload,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminUser = data
                return data
            } catch {
                this.adminUser = null
            }
        },

        async deleteAdminUser(user_id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminUsers>(`/api/admin/users/${user_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminUsers = data
                return data
            } catch {
                this.adminUsers = null
            }
        },

        async fetchAdminOrders() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminOrders>('/api/admin/orders', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminOrders = data
                return data
            } catch {
                this.adminOrders = null
            }
        },

        async fetchUserOrdersSSR() {
            try {
                const { $api } = useNuxtApp()
                this.orders = await $api<Orders>('/api/user/orders')
            } catch {
                this.orders = null
            }
        },

        async updateProfile(payload: UpdateProfilePayload) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                await this.getCsrfCookie()
                const updated = await $api<User>('/api/user', {
                    method: 'PUT',
                    body: payload,
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-XSRF-TOKEN': useXsrfToken() || '',
                    },
                })
                this.user = updated
                return updated
            } catch (err: any) {
                this.error = err?.data?.message || 'Profile update failed'
                throw err
            } finally {
                this.loading = false
            }
        },

        async emailExists(email: string): Promise<boolean> {
            try {
                const { $api } = useNuxtApp()
                const res = await $api<{ exists: boolean }>('/api/user/email-exists', {
                    method: 'GET',
                    params: { email },
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                return !!res?.exists
            } catch {
                return false
            }
        },

        async getAudioObjectUrl(signedUrl: string): Promise<string> {
            // const cached = this._audioObjectUrls[signedUrl]
            // if (cached) return cached
            //
            // const { $api } = useNuxtApp()
            // const blob = await $api<Blob>(signedUrl, {
            //     method: 'GET',
            //     credentials: 'include',
            //     responseType: 'blob',
            //     headers: { Accept: 'audio/mpeg' },
            // })
            // const objectUrl = URL.createObjectURL(blob)
            // this._audioObjectUrls[signedUrl] = objectUrl
            // return objectUrl
            return signedUrl
        },

        revokeAudioObjectUrl(signedUrl: string) {
            const url = this._audioObjectUrls[signedUrl]
            if (url) {
                URL.revokeObjectURL(url)
                delete this._audioObjectUrls[signedUrl]
            }
        },

        revokeAllAudioObjectUrls() {
            for (const url of Object.values(this._audioObjectUrls)) {
                URL.revokeObjectURL(url)
            }
            this._audioObjectUrls = {}
        },

        async fetchAdminCoupons() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminCoupons>('/api/admin/coupons', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminCoupons = data
                return data
            } catch {
                this.adminCoupons = null
            }
        },

        async createAdminCoupon(payload: CreateUpdateAdminCouponPayload) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminCoupons>('/api/admin/coupons', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    method: 'POST',
                    body: payload,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminCoupons = data
                return data
            } catch {
                this.adminCoupons = null
            }
        },

        async fetchAdminCoupon(id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminCoupon>(`/api/admin/coupons/${id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminCoupon = data
                return data
            } catch {
                this.adminCoupon = null
            }
        },

        async updateAdminCoupon(id: number, payload: CreateUpdateAdminCouponPayload) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminCoupons>(`/api/admin/coupons/${id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    method: 'PUT',
                    body: payload,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminCoupons = data
                return data
            } catch {
                this.adminCoupons = null
            }
        },

        async deleteAdminCoupon(id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminCoupons>(`/api/admin/coupons/${id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminCoupons = data
                return data
            } catch {
                this.adminCoupons = null
            }
        },

        async fetchAdminProducts() {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminProducts>('/api/admin/products', {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminProducts = data
                return data
            } catch {
                this.adminProducts = null
                return null
            }
        },

        async fetchAdminProduct(product_id: number) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminProduct>(`/api/admin/products/${product_id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminProduct = data
                return data
            } catch {
                this.adminProduct = null
                return null
            }
        },

        async updateAdminProduct(id: number, payload: UpdateAdminProductPayload) {
            try {
                const { $api } = useNuxtApp()
                const data = await $api<AdminProduct>(`/api/admin/products/${id}`, {
                    baseURL: useRuntimeConfig().public.apiBase,
                    method: 'PUT',
                    body: payload,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                this.adminProduct = data
                return data
            } catch {
                this.adminProduct = null
                return null
            }
        },

        async uploadAdminPointAudio(
            productId: number,
            pointId: number,
            language: 'sl' | 'en',
            file: File
        ) {
            try {
                const { $api } = useNuxtApp()

                const formData = new FormData()
                formData.append('language', language)
                formData.append('file', file)

                const data = await $api(`/api/admin/products/${productId}/points/${pointId}/audio`, {
                    method: 'POST',
                    baseURL: useRuntimeConfig().public.apiBase,
                    body: formData,
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })

                return data
            } catch (e) {
                // you can add toast/log here if you want
                throw e
            }
        },


        async logout() {
            const { $api } = useNuxtApp()
            await $api('/logout', {
                method: 'POST',
                baseURL: useRuntimeConfig().public.apiBase,
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'X-XSRF-TOKEN': useXsrfToken() || '',
                },
            })
            this.revokeAllAudioObjectUrls()
            this.user = null

            await $api('/api/cart/clear-cookie', {
                method: 'POST',
                baseURL: useRuntimeConfig().public.apiBase,
                credentials: 'include'
            })

            const cart = useCartStore()
            cart.$reset()
        },
    },
})
